import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { MapPin, Plus } from "lucide-react";
import { fetchServiceability, fetchCityDemand, upsertServiceability } from "@admin/services/adminApi";
import { PageHeader, Pill } from "@admin/components/Primitives";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { CityPicker } from "@/components/CityPicker";

const TIER_COLORS = { 1: "green", 2: "amber", 3: "slate" };

function TierSelect({ value, onChange, testid }) {
  return (
    <select value={value} onChange={(e) => onChange(Number(e.target.value))} data-testid={testid}
            className="h-9 rounded-md border border-slate-200 px-2 text-sm bg-white">
      <option value={1}>Tier 1</option>
      <option value={2}>Tier 2</option>
      <option value={3}>Tier 3</option>
    </select>
  );
}

export default function AdminServiceability() {
  const [items, setItems] = useState([]);
  const [demand, setDemand] = useState([]);
  const [picked, setPicked] = useState(null);
  const [tier, setTier] = useState(1);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      setItems((await fetchServiceability()).items || []);
      setDemand((await fetchCityDemand()).items || []);
    }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not load serviceability"); }
  };
  useEffect(() => { load(); }, []);

  const save = async (locationId, payload) => {
    setSaving(true);
    try {
      const doc = await upsertServiceability(locationId, payload);
      setItems((prev) => {
        const rest = prev.filter((i) => i.location_id !== locationId);
        return [...rest, doc].sort((a, b) => (a.name || "").localeCompare(b.name || ""));
      });
      toast.success("Serviceability updated");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
    finally { setSaving(false); }
  };

  const addLocation = async () => {
    if (!picked?.city_id) { toast.error("Search and select a city first"); return; }
    await save(picked.city_id, { tier, lawyer_onboarding_enabled: true });
    setPicked(null);
    setTier(1);
  };

  return (
    <div data-testid="admin-serviceability-page">
      <PageHeader
        title="Serviceable Cities"
        subtitle="Control which cities and city groups are open for lawyer onboarding. Reference data is never modified."
        testid="serviceability-header"
      />

      <div className="bg-white border border-slate-200 rounded-xl p-4 mb-6 grid gap-3 sm:grid-cols-[1fr_auto_auto] sm:items-end">
        <div>
          <Label className="text-xs">Search reference city / city group</Label>
          <CityPicker
            testid="svc-city"
            value={picked}
            onChange={(c) => setPicked(c ? { city_id: c.id, city_name: c.name, state_name: c.state_name } : null)}
          />
        </div>
        <div>
          <Label className="text-xs">Tier</Label>
          <div className="mt-1"><TierSelect value={tier} onChange={setTier} testid="svc-add-tier" /></div>
        </div>
        <Button onClick={addLocation} disabled={saving} data-testid="svc-add-btn" className="h-9">
          <Plus size={14} className="mr-1" /> Enable location
        </Button>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl overflow-x-auto">
        <table className="w-full text-sm" data-testid="svc-table">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wider text-slate-400 border-b border-slate-100">
              <th className="px-4 py-3">Location</th>
              <th className="px-4 py-3">Type</th>
              <th className="px-4 py-3">Tier</th>
              <th className="px-4 py-3">Onboarding</th>
              <th className="px-4 py-3">Enabled at</th>
              <th className="px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-400" data-testid="svc-empty">
                No serviceable locations configured yet. Search a city above to enable it.
              </td></tr>
            )}
            {items.map((it) => (
              <tr key={it.location_id} className="border-b border-slate-50" data-testid={`svc-row-${it.location_id}`}>
                <td className="px-4 py-3">
                  <span className="flex items-center gap-2 font-semibold text-slate-700">
                    <MapPin size={13} className="text-slate-400" />
                    {it.name}{it.state_name && it.state_name !== it.name ? `, ${it.state_name}` : ""}
                  </span>
                  <span className="text-[10px] text-slate-400 ml-6">{it.location_id}</span>
                </td>
                <td className="px-4 py-3">
                  <Pill color={it.location_type === "city_group" ? "blue" : "slate"}>
                    {it.location_type === "city_group" ? "City group" : "City"}
                  </Pill>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <Pill color={TIER_COLORS[it.tier] || "slate"}>Tier {it.tier}</Pill>
                    <TierSelect value={it.tier} testid={`svc-tier-${it.location_id}`}
                                onChange={(t) => save(it.location_id, { tier: t, lawyer_onboarding_enabled: it.lawyer_onboarding_enabled })} />
                  </div>
                </td>
                <td className="px-4 py-3">
                  <Pill color={it.lawyer_onboarding_enabled ? "green" : "red"}>
                    {it.lawyer_onboarding_enabled ? "Enabled" : "Disabled"}
                  </Pill>
                </td>
                <td className="px-4 py-3 text-xs text-slate-500">
                  {it.enabled_at ? new Date(it.enabled_at).toLocaleDateString() : "—"}
                </td>
                <td className="px-4 py-3">
                  <Button size="sm" variant={it.lawyer_onboarding_enabled ? "outline" : "default"}
                          disabled={saving} data-testid={`svc-toggle-${it.location_id}`}
                          onClick={() => save(it.location_id, { tier: it.tier, lawyer_onboarding_enabled: !it.lawyer_onboarding_enabled })}>
                    {it.lawyer_onboarding_enabled ? "Disable" : "Enable"}
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-8" data-testid="city-demand-section">
        <h2 className="text-base md:text-lg font-bold text-[#0A2540] mb-1">Expansion demand</h2>
        <p className="text-xs text-slate-500 mb-3">
          Cities lawyers searched for during onboarding that are not yet serviceable — real demand for your next tier.
        </p>
        <div className="bg-white border border-slate-200 rounded-xl overflow-x-auto">
          <table className="w-full text-sm" data-testid="demand-table">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-slate-400 border-b border-slate-100">
                <th className="px-4 py-3">City</th>
                <th className="px-4 py-3">Searches</th>
                <th className="px-4 py-3">Last searched</th>
                <th className="px-4 py-3">Action</th>
              </tr>
            </thead>
            <tbody>
              {demand.length === 0 && (
                <tr><td colSpan={4} className="px-4 py-6 text-center text-slate-400" data-testid="demand-empty">
                  No unmet demand recorded yet.
                </td></tr>
              )}
              {demand.map((d) => (
                <tr key={d.location_id} className="border-b border-slate-50" data-testid={`demand-row-${d.location_id}`}>
                  <td className="px-4 py-3">
                    <span className="font-semibold text-slate-700">{d.name}{d.state_name ? `, ${d.state_name}` : ""}</span>
                    <span className="text-[10px] text-slate-400 ml-2">{d.location_id}</span>
                  </td>
                  <td className="px-4 py-3"><Pill color="amber">{d.search_count}</Pill></td>
                  <td className="px-4 py-3 text-xs text-slate-500">
                    {d.last_searched_at ? new Date(d.last_searched_at).toLocaleString() : "—"}
                  </td>
                  <td className="px-4 py-3">
                    <Button size="sm" disabled={saving} data-testid={`demand-enable-${d.location_id}`}
                            onClick={() => save(d.location_id, { tier: 2, lawyer_onboarding_enabled: true })}>
                      Enable (Tier 2)
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
